import React, { useCallback, useEffect, useState } from 'react';
import InfoTip from '../InfoTip';
import { ArrowRight, Check, ChevronLeft, ChevronRight, RefreshCw, TriangleAlert } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Avatar from '../epics/Avatar';
import JiraKeyChip from '../JiraKeyChip';
import { typeBadge } from '../hygiene/types';
import FilterChip from '../flow/FilterChip';
import StatusPill from '../flow/StatusPill';
import { CarryRow, ReportNumbers, ReviewRow, SprintDetail, SprintsData, plural } from './types';
import * as fmt from '../../lib/format';
import { idParam, quarterParam } from '../../utils/urlParams';
import { useUrlState } from '../../hooks/useUrlState';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const CAPTION = 'text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500';
const ICON_BUTTON =
  'rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] disabled:opacity-30 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const PAGE = 30;

interface Props {
  projectKey: string | null;
  refreshKey: number;
}

const STATE_LABEL: Record<string, string> = { closed: 'Closed', active: 'Active', future: 'Not started' };

// ─── Header and notices ─────────────────────────────────────────────────────────

function Notice({ children, tone = 'amber' }: { children: React.ReactNode; tone?: 'amber' | 'blue' }) {
  const palette = tone === 'amber' ? 'bg-amber-50 text-amber-900 ring-amber-200' : 'bg-blue-50 text-blue-900 ring-blue-200';
  return <div className={`mt-4 flex items-start gap-2 rounded-xl px-4 py-2.5 text-sm ring-1 ${palette}`}>{children}</div>;
}

function SprintHeader({ detail, isAdmin, onReread, rereading }: { detail: SprintDetail; isAdmin: boolean; onReread: () => void; rereading: boolean }) {
  const { sprint, report } = detail;
  const state = sprint.state ?? 'closed';
  const badge =
    sprint.overdue ? 'Open past its end date' : STATE_LABEL[state] ?? state;
  const tone = sprint.overdue ? 'bg-amber-50 text-amber-900 ring-amber-200' : state === 'active' ? 'bg-blue-50 text-blue-800 ring-blue-200' : state === 'future' ? 'bg-gray-100 text-gray-700 ring-gray-200' : 'bg-emerald-50 text-emerald-800 ring-emerald-200';
  return (
    <section className={`${CARD} mb-5 p-5`} aria-label="Sprint">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-snug tracking-tight text-gray-900">{sprint.name}</h2>
          <p className="mt-0.5 text-sm text-gray-600">
            {sprint.project_name ?? sprint.project_key} · {fmt.dayMonth(sprint.start)} – {fmt.dayMonth(sprint.end)}
            {sprint.complete ? ` · closed ${fmt.dayMonth(sprint.complete)}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button type="button" onClick={onReread} disabled={rereading} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200 transition-[background-color,transform] hover:bg-gray-50 active:scale-[0.97] disabled:opacity-50 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
              <RefreshCw className={`h-3.5 w-3.5 ${rereading ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden />
              Read again from Jira
            </button>
          )}
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${tone}`}>{badge}</span>
        </div>
      </div>

      {sprint.overdue && (
        <Notice>
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>This sprint ended on {fmt.dayMonth(sprint.end)} and is still open in Jira. Close it so what was not completed is carried over.</span>
        </Notice>
      )}
      {report.not_started && (
        <Notice tone="blue">
          <span>
            Jira has not started this sprint yet. {fmt.count(report.planned)} {plural(report.planned, 'ticket is', 'tickets are')} planned in it. Its numbers appear once it starts.
          </span>
        </Notice>
      )}
      {report.error && (
        <Notice>
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            Jira’s Sprint Report could not be read ({report.error}).{' '}
            {report.stale && report.fetched_at ? `Showing the report read on ${fmt.dayMonth(report.fetched_at)}.` : 'The numbers are not available right now.'}
          </span>
        </Notice>
      )}
    </section>
  );
}

// ─── The numbers ────────────────────────────────────────────────────────────────

function Tile({ label, value, note }: { label: string; value: number; note: string }) {
  return (
    <div>
      <dt className={CAPTION}>{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-gray-900">{fmt.count(value)}</dd>
      <dd className="mt-0.5 text-xs leading-snug text-gray-500">{note}</dd>
    </div>
  );
}

function ScopeCard({ numbers, open, fetchedAt }: { numbers: ReportNumbers; open: boolean; fetchedAt: string | null }) {
  const creep = numbers.scope_creep_pct;
  const over = numbers.over_threshold;
  return (
    <section className={`${CARD} mb-5 p-5`} aria-label="Sprint numbers">
      <div className="grid gap-6 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
        <div>
          <div className="flex items-center">
            <h3 className={CAPTION}>Added after the start{open ? ' so far' : ''}</h3>
            <InfoTip id="planning.scope-creep" />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-4xl font-semibold tabular-nums tracking-tight text-gray-900">{fmt.percent(creep)}</span>
            {over !== null && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                  over ? 'bg-amber-50 text-amber-900 ring-amber-200' : 'bg-emerald-50 text-emerald-800 ring-emerald-200'
                }`}
              >
                {over ? <TriangleAlert className="h-3 w-3" aria-hidden /> : <Check className="h-3 w-3" aria-hidden />}
                {over ? `Above the ${fmt.percent(numbers.threshold_pct)} limit` : `Within the ${fmt.percent(numbers.threshold_pct)} limit`}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-sm leading-snug text-gray-600">
            {numbers.committed === 0
              ? 'Nothing was committed: Jira counts every issue added after it started the sprint. Start the sprint once planning is done to see what was added after the start.'
              : `${fmt.count(numbers.added)} ${plural(numbers.added, 'issue was', 'issues were')} added after the sprint started, on top of ${fmt.count(numbers.committed)} committed.`}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-5">
          <Tile label="Committed" value={numbers.committed} note="in the sprint when it started" />
          <Tile label="Added" value={numbers.added} note="after it started" />
          <Tile label="Removed" value={numbers.removed} note="taken out again" />
          <Tile label="Completed" value={numbers.completed} note={open ? 'done so far' : 'done by the end'} />
          <Tile label={open ? 'Open' : 'Not completed'} value={numbers.not_completed} note={open ? 'still to do' : 'left open at the end'} />
        </dl>
      </div>
      <p className="mt-4 text-xs text-gray-500">
        Counts as Jira’s Sprint Report shows them, so they match what the team sees there
        {numbers.completed_elsewhere > 0 ? `; ${numbers.completed_elsewhere} more ${plural(numbers.completed_elsewhere, 'was', 'were')} finished in another sprint` : ''}
        {fetchedAt ? ` · read ${fmt.dayMonth(fetchedAt)}` : ''}.
      </p>
    </section>
  );
}

// ─── Lists ──────────────────────────────────────────────────────────────────────

function Rollover({ count, reason, review }: { count: number | null; reason: string | null; review: boolean }) {
  if (count === null && !reason && !review) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
      {count !== null && count > 0 && <span className="text-xs text-gray-600">Carried over {count}×{reason ? ` · ${reason}` : ''}</span>}
      {count === null && reason && <span className="text-xs text-gray-600">{reason}</span>}
      {review && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-200">
          <TriangleAlert className="h-3 w-3" aria-hidden />
          Needs a lead review
        </span>
      )}
    </div>
  );
}

function CarryList({ detail }: { detail: SprintDetail }) {
  const c = detail.carry_over;
  const [limit, setLimit] = useState(PAGE);
  const open = c.kind === 'open';
  const shown = c.items.slice(0, limit);
  if (c.total === 0) {
    return (
      <div className={`${CARD} mb-5 px-5 py-12 text-center text-sm text-gray-500`}>
        {open ? 'Nothing is open in this sprint.' : 'Everything in this sprint was completed.'}
      </div>
    );
  }
  return (
    <section className={`${CARD} mb-5 overflow-hidden`} aria-label={open ? 'Still open' : 'Not completed'}>
      <div className="p-5 pb-4">
        <div className="flex items-center">
          <h3 className={CAPTION}>{open ? 'Still open' : 'Not completed'} · {fmt.count(c.total)}</h3>
          {!open && <InfoTip id="planning.rollover" />}
        </div>
        {!open && (
          <p className="mt-1 text-sm text-gray-600">
            {c.finished_since} finished since · {c.moved_on} moved to a later sprint · {c.back_to_backlog} back to the backlog
          </p>
        )}
        {!open && c.by_reason.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Carry-over reasons">
            {c.by_reason.map((r) => (
              <span key={r.reason} className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-1 text-xs text-gray-700 ring-1 ring-inset ring-gray-200">
                {r.reason}
                <span className="font-semibold tabular-nums">{r.count}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <ul className="divide-y divide-gray-100 border-t border-gray-100">
        {shown.map((r) => <CarryItem key={r.issue_key} row={r} open={open} />)}
      </ul>
      {c.items.length > shown.length && (
        <div className="border-t border-gray-100 p-2">
          <button type="button" onClick={() => setLimit((n) => n + PAGE)} className="w-full rounded-xl px-3 py-2 text-sm font-medium text-blue-700 transition-[background-color,transform] duration-150 hover:bg-blue-50/70 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
            Show {Math.min(PAGE, c.items.length - shown.length)} more <span className="text-gray-500">· {c.items.length - shown.length} left</span>
          </button>
        </div>
      )}
    </section>
  );
}

function CarryItem({ row, open }: { row: CarryRow; open: boolean }) {
  return (
    <li className="flex items-start justify-between gap-4 px-5 py-3.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <JiraKeyChip issueKey={row.issue_key} url={row.jira_url} />
          {row.type && <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${typeBadge(row.type)}`}>{row.type}</span>}
        </div>
        <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-900" title={row.name ?? undefined}>{row.name || 'Not in the local copy yet'}</p>
        <Rollover count={row.rollover_count} reason={row.rollover_reason} review={row.needs_review} />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
        {row.status && <StatusPill status={row.status} small />}
        {!open && (
          <span className="inline-flex max-w-[12rem] items-center gap-1 text-xs text-gray-600">
            {row.finished_since ? (
              <>
                <Check className="h-3 w-3 shrink-0 text-emerald-700" aria-hidden />Finished since
              </>
            ) : row.moved_to ? (
              <>
                <ArrowRight className="h-3 w-3 shrink-0" aria-hidden />
                <span className="truncate">{row.moved_to.name.replace(/^\s*Q[1-4]\s*FY\s*\d{2,4}\s*[-–—:]*\s*/i, '')}</span>
              </>
            ) : (
              'Back to the backlog'
            )}
          </span>
        )}
        {row.assignee && (
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <Avatar name={row.assignee} size="sm" />
            <span className="max-w-[8rem] truncate">{row.assignee}</span>
          </span>
        )}
      </div>
    </li>
  );
}

function ReviewList({ detail }: { detail: SprintDetail }) {
  const r = detail.needs_review;
  const project = detail.sprint.project_name ?? detail.sprint.project_key ?? 'this project';
  return (
    <section className={`${CARD} overflow-hidden`} aria-label="Needs a lead review">
      <div className="p-5 pb-4">
        <h3 className={CAPTION}>Needs a lead review · {fmt.count(r.total)}</h3>
        <p className="mt-1 text-sm text-gray-600">
          Open tickets in {project} that have been carried over more than {r.threshold} {plural(r.threshold, 'time', 'times')}. The standard asks the Tech Lead and Product Owner to split, re-estimate, re-prioritise, move to the backlog or drop them.
        </p>
      </div>
      {r.items.length === 0 ? (
        <div className="border-t border-gray-100 px-5 py-10 text-center text-sm text-gray-500">No open ticket has been carried over more than {r.threshold} times.</div>
      ) : (
        <ul className="divide-y divide-gray-100 border-t border-gray-100">
          {r.items.map((i: ReviewRow) => (
            <li key={i.issue_key} className="flex items-start justify-between gap-4 px-5 py-3.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <JiraKeyChip issueKey={i.issue_key} url={i.jira_url} />
                  {i.type && <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${typeBadge(i.type)}`}>{i.type}</span>}
                </div>
                <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-900" title={i.name ?? undefined}>{i.name || 'Untitled'}</p>
                <p className="mt-1.5 text-xs text-gray-600">Carried over {i.rollover_count}×{i.rollover_reason ? ` · ${i.rollover_reason}` : ''}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                {i.status && <StatusPill status={i.status} small />}
                {i.assignee && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Avatar name={i.assignee} size="sm" />
                    <span className="max-w-[8rem] truncate">{i.assignee}</span>
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── The Sprint tab ─────────────────────────────────────────────────────────────

export default function SprintView({ projectKey, refreshKey }: Props) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // The quarter and the sprint on show live in the address (?sprint_quarter=2026-Q3&sprint=412) with the project,
  // so a reload or a copied link opens the same sprint.
  const { params, update } = useUrlState();
  const quarterId = quarterParam(params, 'sprint_quarter');
  const sprintId = idParam(params, 'sprint');
  const setSprintId = (id: number) => update({ sprint: String(id) });
  const [list, setList] = useState<SprintsData | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listNote, setListNote] = useState<string | null>(null);
  const [detail, setDetail] = useState<SprintDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rereading, setRereading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The project's sprints for the quarter. Jira is asked first when the stored ones are old.
  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    apiService
      .getPlanningSprints({ project_key: projectKey ?? undefined, quarter: quarterId ?? undefined })
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setList(r.data);
          setListNote(r.meta?.refresh_error ? `The sprint list could not be refreshed from Jira (${r.meta.refresh_error}); showing what is stored.` : null);
          setError(null);
        } else setError(r.error || 'Could not load the sprints');
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the sprints'))
      .finally(() => !cancelled && setListLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectKey, quarterId, refreshKey]);

  // Pick the sprint to show: the one chosen, else the one in progress, else the last of the quarter.
  const sprints = list?.sprints ?? [];
  const defaultId = sprints.find((s) => s.id === list?.current_sprint_id)?.id ?? sprints[sprints.length - 1]?.id ?? null;
  const activeId = sprintId !== null && sprints.some((s) => s.id === sprintId) ? sprintId : defaultId;

  const loadDetail = useCallback(async (id: number, force: boolean) => {
    const r = await apiService.getPlanningSprint(id, { refresh: force });
    if (!r.success) throw new Error(r.error || 'Could not load the sprint');
    return r.data as SprintDetail;
  }, []);

  useEffect(() => {
    if (activeId === null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    loadDetail(activeId, false)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setError(null);
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || e?.message || 'Could not load the sprint'))
      .finally(() => !cancelled && setDetailLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeId, refreshKey, loadDetail]);

  const reread = async () => {
    if (activeId === null) return;
    setRereading(true);
    try {
      setDetail(await loadDetail(activeId, true));
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not read the sprint again');
    } finally {
      setRereading(false);
    }
  };

  const quarters = list?.quarters ?? [];
  const qIndex = quarters.findIndex((q) => q.id === list?.quarter?.id);
  const olderQ = qIndex >= 0 ? quarters[qIndex + 1] : undefined;
  const newerQ = qIndex > 0 ? quarters[qIndex - 1] : undefined;
  const changeQuarter = (id: string) => update({ sprint_quarter: id, sprint: null });

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="sr-only" htmlFor="planning-project">Project</label>
        <select
          id="planning-project"
          value={list?.project?.project_key ?? ''}
          onChange={(e) => update({ project_key: e.target.value || null, sprint_quarter: null, sprint: null })}
          className="max-w-[16rem] rounded-lg bg-white py-1.5 pl-3 pr-9 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 contrast-more:ring-gray-400"
        >
          {(list?.projects ?? []).map((p) => (
            <option key={p.project_key} value={p.project_key}>
              {p.project_key}{p.project_name ? ` · ${p.project_name}` : ''}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous quarter" disabled={!olderQ} onClick={() => olderQ && changeQuarter(olderQ.id)} className={ICON_BUTTON}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[4.5rem] text-center text-sm font-medium text-gray-900">{list?.quarter?.label ?? ''}</span>
          <button type="button" aria-label="Next quarter" disabled={!newerQ} onClick={() => newerQ && changeQuarter(newerQ.id)} className={ICON_BUTTON}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {sprints.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2" role="group" aria-label="Sprints">
          {sprints.map((s) => (
            <FilterChip
              key={s.id}
              label={`${s.label}${s.is_current ? ' · now' : s.state === 'future' ? ' · not started' : s.overdue ? ' · open' : ''}`}
              selected={s.id === activeId}
              onClick={() => setSprintId(s.id)}
              title={`${s.name}: ${fmt.dayMonth(s.start)} – ${fmt.dayMonth(s.end)}`}
            />
          ))}
        </div>
      )}

      {listNote && <div className="mb-4 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-900 ring-1 ring-amber-200">{listNote}</div>}
      {error && <div className="mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">{error}</div>}

      {listLoading && !list ? (
        <div className="space-y-5" role="status" aria-label="Loading the sprints">
          <div className={`${CARD} h-24 animate-pulse motion-reduce:animate-none`} />
          <div className={`${CARD} h-36 animate-pulse motion-reduce:animate-none`} />
        </div>
      ) : !list?.project ? (
        <div className={`${CARD} px-6 py-14 text-center text-sm text-gray-500`}>No project has sprints yet.</div>
      ) : sprints.length === 0 ? (
        <div className={`${CARD} px-6 py-14 text-center text-sm text-gray-500`}>
          {list.project.project_name ?? list.project.project_key} has no sprints in {list.quarter?.label}. Use the arrows to look at another quarter.
        </div>
      ) : detail && detail.sprint.id === activeId ? (
        <div className={detailLoading ? 'opacity-60 transition-opacity motion-reduce:transition-none' : 'transition-opacity motion-reduce:transition-none'} aria-busy={detailLoading}>
          <SprintHeader detail={detail} isAdmin={isAdmin} onReread={reread} rereading={rereading} />
          {detail.report.numbers && <ScopeCard numbers={detail.report.numbers} open={detail.sprint.state !== 'closed'} fetchedAt={detail.report.fetched_at} />}
          {detail.report.numbers && <CarryList key={detail.sprint.id} detail={detail} />}
          <ReviewList detail={detail} />
        </div>
      ) : (
        <div className="space-y-5" role="status" aria-label="Loading the sprint">
          <div className={`${CARD} h-24 animate-pulse motion-reduce:animate-none`} />
          <div className={`${CARD} h-40 animate-pulse motion-reduce:animate-none`} />
        </div>
      )}
    </div>
  );
}
